import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminUpdateUserInput,
  AdminUsersQuery,
  AircraftDto,
  AircraftTypeDto,
  AppSettingsDto,
  CreateAircraftInput,
  CreateAircraftTypeInput,
  CreateInvitationInput,
  CsvImportResultDto,
  FlightOfferDto,
  FlightOfferStatus,
  PaginatedDto,
  UpdateAircraftInput,
  UpdateAircraftTypeInput,
  UpdateAppSettingsInput,
  UserDto,
} from '@wingseat/shared';
import { api } from '../lib/api-client';

// ---------------- Users ----------------

export function useAdminUsers(query: Partial<AdminUsersQuery>) {
  return useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () =>
      api.get<PaginatedDto<UserDto>>('/admin/users', {
        query: {
          status: query.status,
          role: query.role,
          search: query.search,
          page: query.page ?? 1,
          pageSize: query.pageSize ?? 20,
        },
      }),
  });
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; data: AdminUpdateUserInput }) =>
      api.patch<UserDto>(`/admin/users/${vars.id}`, vars.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useImportUsersCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (csv: string) =>
      api.post<CsvImportResultDto>('/admin/users/import', csv, {
        headers: { 'Content-Type': 'text/csv' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] });
    },
  });
}

// ---------------- Invitations ----------------

interface InvitationListItem {
  id: string;
  email: string;
  expiresAt: string;
  usedAt: string | null;
  createdAt: string;
  invitedBy: { id: string; firstName: string; lastName: string } | null;
}

export function useAdminInvitations() {
  return useQuery({
    queryKey: ['admin', 'invitations'],
    queryFn: () => api.get<InvitationListItem[]>('/admin/invitations'),
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvitationInput) =>
      api.post<{ id: string; email: string }>('/admin/invitations', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/admin/invitations/${id}/resend`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] }),
  });
}

// ---------------- Aircraft (Admin sieht inkl. inaktiver) ----------------

export function useAdminAircraft() {
  return useQuery({
    queryKey: ['admin', 'aircraft'],
    queryFn: () => api.get<AircraftDto[]>('/admin/aircraft'),
  });
}

export function useAdminAircraftTypes() {
  return useQuery({
    queryKey: ['admin', 'aircraft-types'],
    queryFn: () => api.get<AircraftTypeDto[]>('/admin/aircraft-types'),
  });
}

function invalidateAircraft(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ['admin', 'aircraft'] });
  qc.invalidateQueries({ queryKey: ['admin', 'aircraft-types'] });
  qc.invalidateQueries({ queryKey: ['aircraft'] });
  qc.invalidateQueries({ queryKey: ['aircraft-types'] });
}

export function useCreateAircraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAircraftInput) =>
      api.post<AircraftDto>('/admin/aircraft', input),
    onSuccess: () => invalidateAircraft(qc),
  });
}

export function useUpdateAircraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; data: UpdateAircraftInput }) =>
      api.patch<AircraftDto>(`/admin/aircraft/${vars.id}`, vars.data),
    onSuccess: () => invalidateAircraft(qc),
  });
}

export function useDeleteAircraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/aircraft/${id}`),
    onSuccess: () => invalidateAircraft(qc),
  });
}

export function useCreateAircraftType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAircraftTypeInput) =>
      api.post<AircraftTypeDto>('/admin/aircraft-types', input),
    onSuccess: () => invalidateAircraft(qc),
  });
}

export function useUpdateAircraftType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; data: UpdateAircraftTypeInput }) =>
      api.patch<AircraftTypeDto>(`/admin/aircraft-types/${vars.id}`, vars.data),
    onSuccess: () => invalidateAircraft(qc),
  });
}

export function useDeleteAircraftType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/aircraft-types/${id}`),
    onSuccess: () => invalidateAircraft(qc),
  });
}

// ---------------- Admin Flights ----------------

export function useAdminFlights(
  status?: FlightOfferStatus,
  includeHidden: boolean = false,
) {
  return useQuery({
    queryKey: ['admin', 'flights', status ?? 'all', { includeHidden }],
    queryFn: () =>
      api.get<FlightOfferDto[]>('/admin/flights', {
        query: { status, includeHidden },
      }),
  });
}

export function useDeactivateFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      api.post<FlightOfferDto>(`/admin/flights/${vars.id}/deactivate`, {
        reason: vars.reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'flights'] });
      qc.invalidateQueries({ queryKey: ['flights'] });
    },
  });
}

export function useHideFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<FlightOfferDto>(`/admin/flights/${id}/hide`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'flights'] });
      qc.invalidateQueries({ queryKey: ['flights'] });
    },
  });
}

export function useUnhideFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<FlightOfferDto>(`/admin/flights/${id}/unhide`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'flights'] });
      qc.invalidateQueries({ queryKey: ['flights'] });
    },
  });
}

// ---------------- Settings ----------------

export function useAdminSettings() {
  return useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.get<AppSettingsDto>('/admin/settings'),
  });
}

export function useUpdateAdminSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAppSettingsInput) =>
      api.patch<AppSettingsDto>('/admin/settings', input),
    onSuccess: (data) =>
      qc.setQueryData(['admin', 'settings'], data),
  });
}
